import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { mesVisitesAPI, monProfilAPI } from '../services/api';
import { ChoixPhotoEnseignant } from './PhotoEnseignant';
import StageVisitePublique from './StageVisitePublique';
import StageBarreProgression from './StageBarreProgression';

/**
 * ENSEIGNANTS — « Mon espace » → « Mes visites de stage » (lot 3.5c-4, E146, E159 → E161)
 * ======================================================================================
 * Maquette : doc/maquettes/enseignants_maquette.html, `#tab-visites` — bandeau d'information, trois cartes
 * (étudiants suivis / visites effectuées / à effectuer), filtres (recherche, formation, statut), tableau
 * « Mes étudiants — Stage AAAA » (Étudiant · Formation · Entreprise · Période · Avancement dossier · Visite ·
 * Note · Actions), légende, cartes sur téléphone ; le clic ouvre la fiche de visite dans un volet.
 *
 * ⚠️ LA MÊME FICHE que par le lien du tuteur (`StageVisitePublique`, E146) : « Remplie par » = le compte (E156),
 * le serveur vérifie sous verrou que le compte est bien le tuteur du dossier (E159).
 * ⚠️ D6 / E160 : le compte est lié à UNE fiche enseignant ; le serveur retrouve celle de l'année affichée par
 * nom + prénom — s'il n'y arrive pas, il le DIT (raison affichée), il ne montre rien d'autre.
 */

const dateFr = (iso) => (iso ? new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR') : '');
const jourMois = (iso) => dateFr(iso).slice(0, 5);
const semaines = (debut, fin) => (debut && fin ? Math.max(1, Math.round((new Date(fin) - new Date(debut)) / (7 * 86400000))) : null);
const normaliser = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const couleurNote = (v) => {
    if (!v || !v.total_max) return '#64748b';
    const r = v.total / v.total_max;
    return r >= 0.8 ? '#16a34a' : r >= 0.6 ? '#2563eb' : r >= 0.4 ? '#d97706' : '#dc2626';
};

function CelluleVisite({ l }) {
    if (l.visite) {
        return (
            <>
                <span className="ens-mv-badge ok"><i className="fa-solid fa-circle-check" /> Effectuée</span>
                <div className="ens-mv-sous">{jourMois(l.visite.date)} · {l.visite.mode_libelle}</div>
            </>
        );
    }
    if (!l.commence) {
        return (<><span className="ens-mv-badge attente"><i className="fa-solid fa-hourglass-half" /> Pas commencé</span>
            <div className="ens-mv-sous">Fiche ouverte le {dateFr(l.ouvre_le)}</div></>);
    }
    return (
        <>
            <span className="ens-mv-badge plan"><i className="fa-solid fa-clock" /> À effectuer</span>
            {l.echeance_visite && <div className="ens-mv-sous" style={{ color: l.en_retard ? '#dc2626' : '#d97706' }}>Avant le {dateFr(l.echeance_visite)}</div>}
        </>
    );
}

/**
 * 2026-10-10 (v74, Q1) : « Ma photo » — l'enseignant dépose LUI-MÊME sa photo (spec Enseignants : « photo / avatar
 * éditables » dans Mon espace). Elle suit la personne d'une année sur l'autre et se voit dans le Stage (Tuteurs,
 * aide à la répartition) et, plus tard, le Planning oraux.
 */
function MaPhoto({ anneeId }) {
    const [moi, setMoi] = useState(null);
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState(null);
    useEffect(() => {
        let vivant = true;
        monProfilAPI.lire(anneeId).then((r) => { if (vivant) setMoi({ ...r.data.enseignant, photo_url: r.data.photo_url }); }).catch(() => { if (vivant) setMoi(null); });
        return () => { vivant = false; };
    }, [anneeId]);
    if (!moi) return null;
    const geste = async (faire) => {
        setEnCours(true); setErreur(null);
        try { const r = await faire(); setMoi((m) => ({ ...m, photo_url: r.data.photo_url || null })); }
        catch (e) { setErreur(e.response?.data?.error || "La photo n'a pas pu être enregistrée"); }
        finally { setEnCours(false); }
    };
    return (
        <div className="ens-mv-maphoto">
            <ChoixPhotoEnseignant e={moi} modifiable enCours={enCours} erreur={erreur}
                aide="Votre photo apparaît auprès de vos collègues du département (tuteurs, répartition). Elle vous suit d’une année sur l’autre — .jpg ou .png, 2 Mo au plus."
                onDeposer={(fichier) => geste(() => monProfilAPI.deposerPhoto(anneeId, fichier))}
                onRetirer={() => geste(() => monProfilAPI.retirerPhoto(anneeId).then(() => ({ data: { photo_url: null } })))} />
        </div>
    );
}

function EnseignantsMesVisites({ anneeId, user }) {
    const [donnees, setDonnees] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [recherche, setRecherche] = useState('');
    const [formation, setFormation] = useState('');
    const [statut, setStatut] = useState('');
    const [ouvert, setOuvert] = useState(null);          // { ligne, page }
    const [erreurFiche, setErreurFiche] = useState(null);

    // ⚠️ Aucun rappel de parent dans ces dépendances (leçon du lot 1.3 bis).
    const recharger = useCallback(async () => {
        setErreur(null);
        try { setDonnees((await mesVisitesAPI.lister(anneeId)).data); }
        catch (e) { setErreur(e.response?.data?.error || 'Lecture de vos visites impossible'); setDonnees(null); }
    }, [anneeId]);
    useEffect(() => { recharger(); }, [recharger]);

    const toutes = useMemo(() => donnees?.lignes || [], [donnees]);
    const formations = useMemo(() => [...new Set(toutes.map((l) => l.formation_nom))], [toutes]);
    const lignes = useMemo(() => {
        const q = normaliser(recherche.trim());
        return toutes.filter((l) => (!formation || l.formation_nom === formation)
            && (!statut || (statut === 'faite' ? !!l.visite : !l.visite))
            && (!q || normaliser(`${l.nom} ${l.prenom} ${l.entreprise_nom || ''}`).includes(q)));
    }, [toutes, formation, statut, recherche]);
    const nbFaites = toutes.filter((l) => l.visite).length;
    const modeles = [...new Set(toutes.filter((l) => l.visite).map((l) => l.visite.modele_nom))];

    const ouvrir = async (l) => {
        setErreurFiche(null);
        try { setOuvert({ ligne: l, page: (await mesVisitesAPI.lire(l.dossier_id)).data }); }
        catch (e) { setErreurFiche(e.response?.data?.error || 'Ouverture de la fiche impossible'); }
    };
    const fermer = useCallback(() => { setOuvert(null); recharger(); }, [recharger]);
    useEffect(() => {
        if (!ouvert) return undefined;
        const touche = (e) => { if (e.key === 'Escape') fermer(); };
        window.addEventListener('keydown', touche);
        return () => window.removeEventListener('keydown', touche);
    }, [ouvert, fermer]);

    if (erreur) return <div className="ens-content"><div className="ens-alert ens-alert-error">{erreur}</div></div>;
    if (!donnees) return <div className="ens-content"><div className="ens-center"><i className="fa-solid fa-spinner fa-spin ens-spinner" /><p>Chargement…</p></div></div>;
    if (!donnees.enseignant) {
        return (
            <div className="ens-content">
                <div className="ens-mv-info"><i className="fa-solid fa-circle-info" /><div>{donnees.raison}</div></div>
            </div>
        );
    }
    const e = donnees.enseignant;
    const nomTuteur = `${e.nom} ${e.prenom || ''}`.trim();
    const libelleAnnee = donnees.annee || '';

    return (
        <div className="ens-content ens-mv">
            <MaPhoto anneeId={anneeId} />
            <div className="ens-mv-info">
                <i className="fa-solid fa-circle-info" />
                <div>
                    Vous êtes tuteur pédagogique de <strong>{toutes.length} étudiant{toutes.length > 1 ? 's' : ''}</strong> pour {libelleAnnee || 'cette année'}
                    {formations.length ? ` (${formations.join(', ')})` : ''}. La fiche de visite est configurée dans
                    <strong> Suivi stages → Paramétrage → Modèles → Fiche de visite</strong>.
                </div>
            </div>

            <div className="ens-stats">
                <div className="ens-stat-card" style={{ borderLeftColor: '#7c3aed' }} onClick={() => setStatut('')}>
                    <div className="ens-stat-count" style={{ color: '#7c3aed' }}>{toutes.length}</div>
                    <div className="ens-stat-label"><i className="fa-solid fa-user-graduate" /> Étudiants suivis</div>
                </div>
                <div className="ens-stat-card" style={{ borderLeftColor: '#16a34a' }} onClick={() => setStatut('faite')}>
                    <div className="ens-stat-count" style={{ color: '#16a34a' }}>{nbFaites}</div>
                    <div className="ens-stat-label"><i className="fa-solid fa-circle-check" /> Visites effectuées</div>
                </div>
                <div className="ens-stat-card" style={{ borderLeftColor: '#d97706' }} onClick={() => setStatut('afaire')}>
                    <div className="ens-stat-count" style={{ color: '#d97706' }}>{toutes.length - nbFaites}</div>
                    <div className="ens-stat-label"><i className="fa-solid fa-hourglass-half" /> Visite{toutes.length - nbFaites > 1 ? 's' : ''} à effectuer</div>
                </div>
            </div>

            <div className="ens-toolbar">
                <div className="ens-search-wrap">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input className="ens-search" placeholder="Rechercher un étudiant…" value={recherche} onChange={(ev) => setRecherche(ev.target.value)} aria-label="Rechercher un étudiant" />
                </div>
                <select className="ens-filter-select" value={formation} onChange={(ev) => setFormation(ev.target.value)} aria-label="Formation">
                    <option value="">Toutes les formations</option>
                    {formations.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <select className="ens-filter-select" value={statut} onChange={(ev) => setStatut(ev.target.value)} aria-label="Statut de la visite">
                    <option value="">Tous les statuts</option>
                    <option value="afaire">À effectuer</option>
                    <option value="faite">Effectuée</option>
                </select>
            </div>
            {erreurFiche && <div className="ens-alert ens-alert-error">{erreurFiche}</div>}

            {/* Tableau (écran large) */}
            <div className="ens-table-wrap ens-mv-table">
                <div className="ens-mv-entete">
                    <span>Mes étudiants — Stage {libelleAnnee}</span>
                    <span className="ens-mv-sous">Tuteur : {nomTuteur}{modeles.length ? ` · Modèle : ${modeles.map((m) => `« ${m} »`).join(', ')}` : ''}</span>
                </div>
                <table className="ens-table">
                    <thead>
                        <tr><th>Étudiant</th><th>Formation</th><th>Entreprise</th><th>Période</th><th>Avancement dossier</th><th>Visite</th><th>Note</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                        {!lignes.length && <tr><td colSpan={8} className="ens-empty-cell">{toutes.length ? 'Aucun étudiant ne correspond.' : "Aucun étudiant ne vous est affecté pour l'instant."}</td></tr>}
                        {lignes.map((l) => (
                            <tr key={l.dossier_id} className="ens-mv-ligne" onClick={() => ouvrir(l)}>
                                <td><strong>{l.nom} {l.prenom}</strong>{l.email && <div className="ens-mv-sous">{l.email}</div>}</td>
                                <td><span className="ens-mv-badge formation">{l.formation_nom}</span></td>
                                <td><strong>{l.entreprise_nom || '—'}</strong>{l.maitre && <div className="ens-mv-sous">{l.maitre}</div>}</td>
                                <td style={{ fontSize: 11 }}>{jourMois(l.date_debut)} → {jourMois(l.date_fin)}
                                    <div className="ens-mv-sous">{l.semestre}{semaines(l.date_debut, l.date_fin) ? ` — ${semaines(l.date_debut, l.date_fin)} sem.` : ''}</div></td>
                                <td><StageBarreProgression segments={l.progression} faites={l.progression_faites} total={l.progression_total} compact /></td>
                                <td><CelluleVisite l={l} /></td>
                                <td>{l.visite ? <><strong style={{ color: couleurNote(l.visite), fontSize: 13 }}>{l.visite.total}</strong><span className="ens-mv-sous">/{l.visite.total_max}</span></> : <span className="ens-mv-sous">—</span>}</td>
                                <td onClick={(ev) => ev.stopPropagation()}>
                                    <button type="button" className={'ens-action-btn ens-mv-action' + (l.visite ? ' voir' : ' saisir')}
                                            title={l.visite ? 'Voir la fiche de visite' : l.commence ? 'Saisir la fiche de visite' : 'Voir la fiche (pas encore ouverte)'}
                                            onClick={() => ouvrir(l)}>
                                        <i className={'fa-solid ' + (l.visite ? 'fa-eye' : 'fa-file-pen')} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="ens-mv-legende">
                    <span><i style={{ background: '#16a34a' }} /> Étape complète</span>
                    <span><i style={{ background: '#f59e0b' }} /> En cours / Action requise</span>
                    <span><i style={{ background: '#e2e8f0' }} /> À venir</span>
                    <span style={{ marginLeft: 'auto' }}>Cliquer sur une ligne pour ouvrir la fiche de visite</span>
                </div>
            </div>

            {/* Cartes (téléphone) */}
            <div className="ens-mv-cartes">
                {lignes.map((l) => (
                    <div key={l.dossier_id} className={'ens-mv-carte' + (!l.visite && l.commence ? ' a-faire' : '')} onClick={() => ouvrir(l)}>
                        <div className="ens-mv-carte-tete">
                            <div><div className="ens-mv-carte-nom">{l.nom} {l.prenom}</div>
                                <div className="ens-mv-sous">{l.entreprise_nom || 'Entreprise à préciser'}{l.maitre ? ` · ${l.maitre}` : ''}</div></div>
                            <span className="ens-mv-badge formation">{l.formation_nom}</span>
                        </div>
                        <StageBarreProgression segments={l.progression} faites={l.progression_faites} total={l.progression_total} compact />
                        <div className="ens-mv-carte-pied">
                            <span><CelluleVisite l={l} /></span>
                            {l.visite && <strong style={{ color: couleurNote(l.visite) }}>{l.visite.total}/{l.visite.total_max}</strong>}
                        </div>
                        <button type="button" className="ens-btn ens-btn-primary ens-mv-carte-btn">
                            <i className="fa-solid fa-file-pen" /> {l.visite ? 'Voir la fiche de visite' : 'Saisir la fiche de visite'}
                        </button>
                    </div>
                ))}
            </div>

            {/* Le volet : LA MÊME fiche que par le lien (E146) */}
            {ouvert && (
                <div className="ens-mv-fond" onClick={fermer}>
                    <div className="ens-mv-volet" onClick={(ev) => ev.stopPropagation()} role="dialog" aria-label={`Fiche de visite — ${ouvert.ligne.nom} ${ouvert.ligne.prenom}`}>
                        <div className="ens-mv-volet-tete">
                            <h3><i className="fa-solid fa-car" /> Fiche de visite — {ouvert.ligne.nom} {ouvert.ligne.prenom}</h3>
                            <button type="button" className="ens-action-btn" onClick={fermer} title="Fermer (Échap)"><i className="fa-solid fa-xmark" /></button>
                        </div>
                        <div className="ens-mv-volet-corps">
                            <StageVisitePublique key={ouvert.ligne.dossier_id} dansVolet page={ouvert.page}
                                remplieParImposee={user?.nom_affiche || [user?.prenom, user?.nom].filter(Boolean).join(' ') || 'Mon compte'}
                                enregistrer={(corps) => mesVisitesAPI.enregistrer(ouvert.ligne.dossier_id, corps)}
                                recharger={() => mesVisitesAPI.lire(ouvert.ligne.dossier_id).then((r) => r.data)} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EnseignantsMesVisites;
